import React, { useState, useMemo } from "react";
import { Modal, Paper, Button, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import "components/pages/transmissions/modal/process/ProcessModal.css";
import LoadingSpinner from "components/LoadingSpinner";
import { useSelector, useDispatch } from "react-redux";

import {
  changeSelectedRows,
  toggleModal,
  toggleProcessing,
} from "app/store/transmission/slice/transmissionSlice";
import { useProcessReportsMutation } from "app/store/transmission/api/transmissionTableApi";
import {
  showErrorToast,
  showSuccessToast,
  showToastWithUndo,
} from "utils/toast";
import SignProcessDataReviewModal from "../sign-process/SignProcessDataReviewModal";
import { REVIEW_MODAL_TITLES } from "static/constants";

const ProcessModal = ({ setRowSelection }) => {
  const {
    totalSelectedRows,
    selectedReports,
    selectedTab,
    isProcessModal,
    isProcessing,
    processModalState,
    reportList,
  } = useSelector((state) => state.transmission);
  const dispatch = useDispatch();
  const [processReports] = useProcessReportsMutation();
  const [showReviewModal, setShowReviewModal] = useState(true);

  const hasAnyNurseFlag = useMemo(() => {
    return selectedReports?.some((reportId) => {
      const report = reportList?.find((r) => r._id === reportId);
      return report?.nurse_flag;
    });
  }, [selectedReports, reportList]);

  const getTabForUndo = (selectedTab, processState) => {
    let tab = null;
    if (selectedTab === "active" && processState === "process") {
      tab = "processed";
    } else if (selectedTab === "processed" && processState === "unprocess") {
      tab = "active";
    } else if (selectedTab === "clinic" && processState === "process") {
      tab = "clinic-review";
    } else if (
      selectedTab === "clinic-review" &&
      processState === "unprocess"
    ) {
      tab = "clinic";
    } else if (selectedTab === "clinic-review" && processState === "process") {
      tab = "clinic-processed";
    }

    return tab;
  };

  const undoAction = async (removedReports, processState) => {
    try {
      const newArr = selectedReports?.filter((report) =>
        removedReports.includes(report)
      );

      const result = await processReports({
        reports: newArr,
        view: getTabForUndo(selectedTab, processState),
        processModalState: processState === "process" ? "unprocess" : "process",
        toReturn: processState === "process" ? false : true,
      }).unwrap();
      const { error, showError } = result;

      if (showError || error) {
        await showErrorToast(result?.message);
      } else {
        showSuccessToast(`Undo Successful`);
      }
    } catch (err) {
      showErrorToast(err?.data?.message);
    }
  };

  const onConfirm = async (reportsToProcess = selectedReports) => {
    const reports = reportsToProcess || selectedReports;
    try {
      dispatch(toggleProcessing(true));
      const result = await processReports({
        reports: reports,
        view: selectedTab,
        processModalState,
        toReturn: false,
      }).unwrap();

      const { data, showError, message } = result;

      const undoProcessAction = async () => {
        await undoAction(data?.removedReports, processModalState);
      };

      if (showError) {
        await showToastWithUndo(result?.message, undoProcessAction, true);
      } else {
        if (data.removedReports?.length > 0) {
          await showToastWithUndo(message, undoProcessAction);
          setRowSelection?.({});
        }
      }
    } catch (e) {
      showErrorToast(e?.message);
    } finally {
      dispatch(toggleProcessing(false));
      dispatch(toggleModal({ type: processModalState, value: false }));
      dispatch(changeSelectedRows({}));
    }
  };

  const onClose = () => {
    dispatch(toggleProcessing(false));
    dispatch(toggleModal({ type: processModalState, value: false }));
    setShowReviewModal(true);
  };

  const handleReviewClose = () => {
    setShowReviewModal(true);
    dispatch(toggleModal({ type: processModalState, value: false }));
  };

  const handleIgnoreAndContinue = () => {
    // Filter out all reports with nurse_flag
    const updatedReports = selectedReports?.filter((reportId) => {
      const report = reportList?.find((r) => r._id === reportId);
      return !report?.nurse_flag;
    });

    // Call onConfirm with filtered reports
    onConfirm(updatedReports);
  };

  const handleBackToReview = () => {
    setShowReviewModal(true);
  };

  const reviewModalTitle =
    REVIEW_MODAL_TITLES[processModalState] ?? "Review Reports";

  return (
    <>
      {/* Data Review Modal - Only show if any report has nurse_flag */}
      {hasAnyNurseFlag && (
        <SignProcessDataReviewModal
          show={isProcessModal && showReviewModal}
          onClose={handleReviewClose}
          onIgnoreAndContinue={handleIgnoreAndContinue}
          isProcessing={isProcessing}
          title={reviewModalTitle}
          action={processModalState}
        />
      )}

      {/* Confirmation Modal */}
      <Modal
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        open={isProcessModal && (!hasAnyNurseFlag || !showReviewModal)}
        onClose={onClose}
      >
        <div className="process-modal__container">
          <Paper>
            <div className="process-modal__wrapper">
              <Tooltip title="Close" placement="bottom">
                <div onClick={onClose} className="process-modal__close">
                  <CloseIcon />
                </div>
              </Tooltip>
              <div className="process-modal__text">
                {!isProcessing && processModalState === "process" && (
                  <div>
                    {totalSelectedRows === 1 && (
                      <div>Are you sure you want to process this record?</div>
                    )}
                    {totalSelectedRows > 1 && (
                      <div>
                        Are you sure you want to process these{" "}
                        {totalSelectedRows} records?
                      </div>
                    )}
                  </div>
                )}
                {!isProcessing &&
                  selectedTab !== "active" &&
                  processModalState === "unprocess" && (
                    <div>
                      {totalSelectedRows === 1 && (
                        <div>
                          Are you sure you want to un-process this record?
                        </div>
                      )}
                      {totalSelectedRows > 1 && (
                        <div>
                          Are you sure you want to un-process these{" "}
                          {totalSelectedRows} records?
                        </div>
                      )}
                    </div>
                  )}
                {isProcessing && processModalState === "process" && (
                  <div>
                    <div className="process-modal__processing">
                      Processing {totalSelectedRows}{" "}
                      {totalSelectedRows > 1 ? "reports" : "report"}.
                    </div>
                    <div className="process-modal__spinner">
                      <LoadingSpinner color="secondary" />
                    </div>
                  </div>
                )}
                {isProcessing && processModalState === "unprocess" && (
                  <div>
                    <div className="process-modal__processing">
                      Un-Processing {totalSelectedRows}{" "}
                      {totalSelectedRows > 1 ? "reports" : "report"}.
                    </div>
                    <div className="process-modal__spinner">
                      <LoadingSpinner color="secondary" />
                    </div>
                  </div>
                )}
              </div>
              <div className="process-modal__buttons">
                {processModalState === "process" && (
                  <Button
                    variant="contained"
                    color="secondary"
                    data-action="bulk-process-button"
                    onClick={() => onConfirm()}
                    disabled={isProcessing}
                  >
                    {isProcessing ? "Processing..." : "Process"}
                  </Button>
                )}
                {processModalState === "unprocess" && (
                  <Button
                    variant="contained"
                    color="secondary"
                    data-action="bulk-unprocess-button"
                    onClick={() => onConfirm()}
                    disabled={isProcessing}
                  >
                    {isProcessing ? "Un-Processing..." : "Un-Process"}
                  </Button>
                )}
                {hasAnyNurseFlag && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleBackToReview}
                    disabled={isProcessing}
                  >
                    Back
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={onClose}
                  disabled={isProcessing}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Paper>
        </div>
      </Modal>
    </>
  );
};

export default ProcessModal;
